import { CloudUpload, Document } from '@carbon/icons-react';
import classNames from 'classnames';
import { TrackingEventList } from 'portal-sdk';
import * as React from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Body,
  Button,
  Heading,
  Icon,
  Loader,
} from 'vet-bones/design-system/components';
import { sendHubspotEvent, usePortalFeatures } from 'vet-bones/utils/portal';

import { refreshDataSources } from 'src/actions/data-sources/action-creators/refreshDataSources';
import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { DataSourceTypeGrid } from 'src/components/dataSourceSelection/DataSourceTypeGrid';
import { useDataSourceTypeList } from 'src/components/dataSourceSelection/hooks/useDataSourceTypeList';
import { PanelProps } from 'src/components/panelStack/PanelStack';
import {
  DataSourceType,
  SAMPLE_MUSIC_DATA_SOURCE_TYPE,
} from 'src/constants/dataSourceTypeDescription';
import { FileExtension } from 'src/constants/fileExtensions';
import { MAX_CSV_UPLOAD_BYTES } from 'src/constants/limits';
import { TEST_IDS } from 'src/constants/testIds';
import { transformModelToSnapshot } from 'src/containers/ProjectDiagram/utils/transformModelToSnapshot';
import { DataSourceHeaderCard } from 'src/containers/ProjectNewResourceDialogV2/components/DataSourceHeaderCard';
import { ProjectResourceCard } from 'src/containers/ProjectNewResourceDialogV2/components/ProjectResourceCard';
import { VirtualGraphResourceCard } from 'src/containers/ProjectNewResourceDialogV2/components/VirtualGraphResourceCard';
import { useProjectResourceDialog } from 'src/containers/ProjectNewResourceDialogV2/context/useProjectResourceDialog';
import { AddDataSourcePanel } from 'src/containers/ProjectNewResourceDialogV2/panels/AddDataSourcePanel';
import { FileUploadPanel } from 'src/containers/ProjectNewResourceDialogV2/panels/FileUploadPanel';
import { useCreateMappings } from 'src/containers/ProjectResourceSuggestionsDialog/hooks/useCreateMappings';
import { useFetchMappingSuggestions } from 'src/containers/ProjectResourceSuggestionsDialog/hooks/useFetchMappingSuggestions';
import { DataSourceCreationPanel } from 'src/containers/ProjectVirtualGraphPreviewDialogV2/panels/DataSourceCreationPanel';
import { connectionSelector } from 'src/selectors/connection';
import { dataSourcesByTypeSelector } from 'src/selectors/dataSourcesByType';
import { isConnectedSelector } from 'src/selectors/isConnected';
import { isConnectingSelector } from 'src/selectors/isConnecting';
import { isRefreshingDataSourcesSelector } from 'src/selectors/isRefreshingDataSources';
import { projectSelector } from 'src/selectors/project';
import {
  ProjectFileUploadOptions,
  ProjectVirtualGraph,
  ProjectVirtualGraphOptions,
} from 'src/store/state/projects';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { FileCreationMetadata } from 'src/types/fileUpload';
import { unparseCsv } from 'src/utils/csv/unparseCsv';
import { generateDateRandomId } from 'src/utils/generateId';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useIsVoiceboxEnabled } from 'src/utils/hooks/useIsVoiceboxEnabled';
import { useNavigate } from 'src/utils/hooks/useNavigate';
import { useProjectMultipleFileUploadsParser } from 'src/utils/hooks/useProjectFileUploadParser';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { useSampleMusicDataSource } from 'src/utils/hooks/useSampleMusicDataSource';
import { useSetupDefaultPortalConnection } from 'src/utils/hooks/useSetupDefaultPortalConnection';
import { readFile } from 'src/utils/readFile';
import { getProjectRoute } from 'src/utils/routes/getProjectRoute';
import { useEvaluateSummary } from 'src/utils/voicebox/hooks/useEvaluateSummary';

import styles from 'src/containers/ProjectNewResourceDialogV2/panels/resourceTypeSelection.module.scss';

export type ResourceTypeSelectionProps = Record<string, never>;

export const ResourceTypeSelection: React.VFC<
  PanelProps<ResourceTypeSelectionProps>
> = ({ openPanel }) => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();
  const { evaluateSummary } = useEvaluateSummary();
  const portalFeatures = usePortalFeatures();
  const useVoiceboxSuggestions = Boolean(
    useIsVoiceboxEnabled() && portalFeatures.voiceboxSuggestions
  );
  const {
    fileMetadata,
    selectedFiles,
    setFileMetadata,
    setSelectedFiles,
    setFooterOptions,
    headerBackButton,
    setHeaderBackButton,
    resetDialogState,
    fullSize,
    setFullSize,
    setDialogTitle,
    // VG state
    selectedDataSource,
    setSelectedDataSource,
    selectedDataSourceType,
    setSelectedDataSourceType,
    checkedVgTables,
    setCheckedVgTables,
    vgTableMetadata,
    setVgTableMetadata,
    customSqlQueries,
    setCustomSqlQueries,
    virtualGraphOptions,
    advancedVgOptions,
  } = useProjectResourceDialog();

  // Data source related hooks and selectors
  const connection = useReduxSelector(connectionSelector);
  const isConnected = useReduxSelector(isConnectedSelector);
  const dataSourcesByType = useReduxSelector(dataSourcesByTypeSelector);
  const isConnecting = useReduxSelector(isConnectingSelector);
  const isRefreshingDataSources = useReduxSelector(
    isRefreshingDataSourcesSelector
  );
  const dataSourceTypes = useDataSourceTypeList();
  const {
    createOrRefresh: createOrRefreshSampleDataSource,
    isLoading: isSampleDataSourceLoading,
    existingDataSource: existingSampleDataSource,
  } = useSampleMusicDataSource();

  const [isCreating, setIsCreating] = React.useState(false);
  const [isCreateAndMapLoading, setIsCreateAndMapLoading] =
    React.useState(false);

  const isMountedRef = useIsMounted();

  // Ensure connection is set up in Redux state and connect if token is available
  useSetupDefaultPortalConnection({ connect: true });

  // Refresh data sources when panel mounts and user is connected (authenticated)
  React.useEffect(() => {
    if (isConnected) {
      dispatch(refreshDataSources());
    }
  }, [dispatch, isConnected]);

  const isLoadingDataSources =
    (isConnecting || isRefreshingDataSources) && dataSourceTypes.length === 0;

  const handleBackToSelection = React.useCallback(() => {
    setSelectedFiles([]);
    setFileMetadata({});
    setFooterOptions({});
    setDialogTitle(
      copy.components.projectResourceDialog.resourceTypePanelV2.titleAdd
    );
  }, [setSelectedFiles, setFileMetadata, setFooterOptions, setDialogTitle]);

  React.useEffect(() => {
    if (selectedFiles.length > 0) {
      setDialogTitle(
        copy.components.projectResourceDialog.resourceTypePanelV2.titleUpload
      );
      if (!headerBackButton) {
        setHeaderBackButton(() => handleBackToSelection);
      }
    } else {
      setDialogTitle(
        copy.components.projectResourceDialog.resourceTypePanelV2.titleAdd
      );
      if (headerBackButton) {
        setHeaderBackButton(null);
      }
    }
  }, [
    selectedFiles.length,
    setHeaderBackButton,
    handleBackToSelection,
    headerBackButton,
    setDialogTitle,
  ]);

  const defaultFileOptions: ProjectFileUploadOptions = React.useMemo(
    () => ({
      escape: '"',
      header: true,
      quote: '"',
      separator: ',',
      skipEmpty: true,
    }),
    []
  );

  const { fieldsByFileIdx, isParsing, rowsByFileIdx } =
    useProjectMultipleFileUploadsParser(
      selectedFiles,
      defaultFileOptions,
      MAX_CSV_UPLOAD_BYTES
    );

  const getValidationError = React.useCallback(
    (fileIndex: number, metadata?: FileCreationMetadata): string => {
      const file = selectedFiles[fileIndex];
      if (!file) {
        return '';
      }

      if (
        !metadata ||
        !Array.isArray(metadata.fields) ||
        typeof metadata.rowCount !== 'number' ||
        !metadata.fileOptions
      ) {
        return '';
      }

      const { fileOptions } = metadata;
      const fileName = metadata.fileName ?? '';

      if (!fileOptions.separator) {
        return copy.components.projectResourceDialog.projectFileUploadPanel
          .validation.separator;
      }
      if (!fileOptions.quote) {
        return copy.components.projectResourceDialog.projectFileUploadPanel
          .validation.quote;
      }
      if (!fileOptions.escape) {
        return copy.components.projectResourceDialog.projectFileUploadPanel
          .validation.escape;
      }

      const rowCount = metadata?.rowCount ?? 0;
      if (rowCount === 0) {
        return copy.components.projectResourceDialog.projectFileUploadPanel
          .validation.empty;
      }

      if (!fileName || fileName.trim() === '') {
        return copy.components.projectResourceDialog.projectFileUploadPanel
          .validation.name;
      }

      return '';
    },
    [selectedFiles]
  );

  const validationErrors = React.useMemo(() => {
    // Skip validation when a sub-panel is open to prevent flash during transitions
    if (fullSize) {
      return {};
    }

    const errors: Record<number, string> = {};
    selectedFiles.forEach((_, index) => {
      const metadata = fileMetadata[index];
      errors[index] = getValidationError(index, metadata);
    });
    return errors;
  }, [selectedFiles, fileMetadata, getValidationError, fullSize]);

  React.useEffect(() => {
    if (isParsing) {
      return;
    }

    setFileMetadata((prevMetadata) => {
      const metadata: Record<number, FileCreationMetadata> = {
        ...prevMetadata,
      };
      let hasChanges = false;

      Object.keys(fieldsByFileIdx).forEach((idx) => {
        const index = Number(idx);
        const newFields = fieldsByFileIdx[index] || [];
        const newRowCount = (rowsByFileIdx[index] || []).length;

        if (
          prevMetadata[index]?.fields !== newFields ||
          prevMetadata[index]?.rowCount !== newRowCount ||
          !prevMetadata[index]?.fileOptions
        ) {
          hasChanges = true;
          metadata[index] = {
            ...prevMetadata[index],
            fields: newFields,
            rowCount: newRowCount,
            fileOptions: prevMetadata[index]?.fileOptions || defaultFileOptions,
            fileName:
              prevMetadata[index]?.fileName ||
              selectedFiles[index]?.name.replace(/\.csv$/i, '') ||
              '',
          };
        }
      });

      return hasChanges ? metadata : prevMetadata;
    });
  }, [
    fieldsByFileIdx,
    isParsing,
    rowsByFileIdx,
    setFileMetadata,
    defaultFileOptions,
    selectedFiles,
  ]);

  const fileIdsRef = React.useRef<Map<File, string>>(new Map());
  const getFileId = React.useCallback((file: File) => {
    let fileId = fileIdsRef.current.get(file);
    if (!fileId) {
      fileId = generateDateRandomId();
      fileIdsRef.current.set(file, fileId);
    }
    return fileId;
  }, []);

  const { getInputProps, getRootProps, isDragActive } = useDropzone({
    accept: FileExtension.CSV,
    multiple: true,
    noClick: false,
    onDropAccepted: (files: File[]) => {
      const newFiles = files.filter((newFile) => {
        return !selectedFiles.some(
          (existingFile) =>
            existingFile.name === newFile.name &&
            existingFile.size === newFile.size &&
            existingFile.lastModified === newFile.lastModified
        );
      });
      setSelectedFiles([...selectedFiles, ...newFiles]);
    },
  });

  const didSelectedFilesChange = useDidValueChange(selectedFiles);

  React.useEffect(() => {
    if (didSelectedFilesChange && selectedFiles.length > 0) {
      const hasPartialFiles = selectedFiles.some(
        (file) => file.size > MAX_CSV_UPLOAD_BYTES
      );
      if (hasPartialFiles) {
        dispatch(projectsActionCreators.uploadedPartialCsv());
      }
    }
  }, [didSelectedFilesChange, selectedFiles, dispatch]);

  const handleOpenFilePanel = (fileIndex: number) => {
    if (openPanel) {
      openPanel({
        props: { fileIndex },
        renderPanel: FileUploadPanel,
        title: copy.components.projectResourceDialog.fileUploadPanelV2.title,
      });
    }
  };

  const { fetchMappingSuggestions } = useFetchMappingSuggestions();
  const handleCreateMappings = useCreateMappings();

  const createFileUploads = React.useCallback(async () => {
    setIsCreating(true);
    try {
      const fileContentsPromises = selectedFiles.map((file) =>
        file.size > MAX_CSV_UPLOAD_BYTES ? Promise.resolve('') : readFile(file)
      );
      const fileContentsArr = await Promise.all(fileContentsPromises);

      const filesToUpload = selectedFiles.map((file, idx) => {
        const isPartial = file.size > MAX_CSV_UPLOAD_BYTES;
        const metadata = fileMetadata[idx];
        const fileOpts = metadata?.fileOptions || defaultFileOptions;

        return {
          name: metadata?.fileName || file.name.replace(/\.csv$/i, ''),
          path: file.name,
          contents:
            isPartial && metadata
              ? unparseCsv({
                  fields: metadata.fields.map(({ value }) => value),
                  rows: rowsByFileIdx[idx] || [],
                  fileOptions: fileOpts,
                })
              : fileContentsArr[idx],
          options: fileOpts,
          fields: metadata?.fields || [],
          id: generateDateRandomId(),
          partial: isPartial,
        };
      });

      dispatch(
        projectsActionCreators.addedFileUploadsToProject(
          projectId,
          filesToUpload
        )
      );

      return filesToUpload.map((file) => file.id);
    } catch (error) {
      console.error('Failed to create file uploads:', error);
      throw error;
    } finally {
      setIsCreating(false);
    }
  }, [
    dispatch,
    fileMetadata,
    projectId,
    rowsByFileIdx,
    selectedFiles,
    defaultFileOptions,
  ]);

  const handleCreate = React.useCallback(
    async () => {
      const fileUploadIds = await createFileUploads();
      if (!isMountedRef.current) {
        return;
      }

      if (fileUploadIds && fileUploadIds.length) {
        sendHubspotEvent(TrackingEventList.DESIGNER_DEFINE_RESOURCE_CSV);
        setSelectedFiles([]);
        setFileMetadata({});
        dispatch(
          uiSlice.actions.toggledProjectResourceDialog({ isOpen: false })
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createFileUploads, dispatch, setSelectedFiles, setFileMetadata]
  );

  const handleCreateAndMap = React.useCallback(
    async () => {
      if (!project?.diagram) {
        return;
      }

      setIsCreateAndMapLoading(true);
      try {
        const fileUploadIds = await createFileUploads();
        if (!isMountedRef.current) {
          return;
        }

        if (fileUploadIds && fileUploadIds.length) {
          sendHubspotEvent(TrackingEventList.DESIGNER_DEFINE_RESOURCE_CSV);
          const loaded = await fetchMappingSuggestions({
            selectedClasses: [],
            selectedResources: fileUploadIds,
          });
          if (!isMountedRef.current) {
            return;
          }

          const snapshot = project?.diagram ? { ...project.diagram } : null;

          if (loaded.mappingSuggestions?.length) {
            const diagram = await handleCreateMappings(
              loaded.mappingSuggestions
            );

            if (snapshot && diagram) {
              snapshot.model = transformModelToSnapshot(diagram.model);
            }
            if (!isMountedRef.current) {
              return;
            }
          }
          setSelectedFiles([]);
          setFileMetadata({});
          dispatch(
            uiSlice.actions.toggledProjectResourceDialog({ isOpen: false })
          );
          if (useVoiceboxSuggestions && project && snapshot) {
            navigate(getProjectRoute(projectId, { rescale: true }));
            evaluateSummary({ ...project, diagram: snapshot });
          }
        }
      } finally {
        if (isMountedRef.current) {
          setIsCreateAndMapLoading(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      createFileUploads,
      dispatch,
      fetchMappingSuggestions,
      handleCreateMappings,
      isMountedRef,
      navigate,
      projectId,
      project,
      setSelectedFiles,
      setFileMetadata,
      useVoiceboxSuggestions,
    ]
  );

  const handleCancel = React.useCallback(() => {
    resetDialogState();
    dispatch(uiSlice.actions.toggledProjectResourceDialog({ isOpen: false }));
  }, [resetDialogState, dispatch]);

  // Merge standard and advanced VG options
  const mergedVirtualGraphOptions = React.useMemo(() => {
    const merged: ProjectVirtualGraphOptions = { ...virtualGraphOptions };
    advancedVgOptions.forEach(({ property, value }) => {
      if (property.trim()) {
        (merged as Record<string, string | boolean | undefined>)[property] =
          value || undefined;
      }
    });
    return merged;
  }, [virtualGraphOptions, advancedVgOptions]);

  // VG create logic
  const createVirtualGraphs = React.useCallback(async () => {
    if (!selectedDataSource || !selectedDataSourceType) {
      return [];
    }

    const virtualGraphsToCreate: Array<
      Omit<ProjectVirtualGraph, 'dateLastUpdated'>
    > = [];

    // Create VG for each checked table
    checkedVgTables.forEach((table) => {
      const id = generateDateRandomId();
      const metadata = vgTableMetadata[table.name];

      // Use edited name if available, otherwise use table name
      const resourceName = metadata?.editedName || table.name;

      // Use edited field labels if available
      const fields = metadata?.fields
        ? metadata.fields.map((field) => ({
            ...field,
            label: metadata.editedFieldLabels?.[field.value] ?? field.label,
          }))
        : [];

      virtualGraphsToCreate.push({
        id,
        name: resourceName,
        dataSourceId: selectedDataSource.id,
        dataSourceType: selectedDataSource.type,
        dataSourceEndpoint: selectedDataSource.options?.['jdbc.url'] || '',
        connectionId: connection?.id || null,
        from: {
          name: table.name,
          text: table.name,
          value: table.name,
          catalog: table.catalog,
          schema: table.schema,
        },
        fields,
        previewRows: [],
        allResultsIncluded: false,
        options: mergedVirtualGraphOptions,
      });
    });

    // Create VG for each custom SQL query
    customSqlQueries.forEach((query) => {
      const id = generateDateRandomId();

      virtualGraphsToCreate.push({
        id,
        name: query.name,
        dataSourceId: selectedDataSource.id,
        dataSourceType: selectedDataSource.type,
        dataSourceEndpoint: selectedDataSource.options?.['jdbc.url'] || '',
        connectionId: connection?.id || null,
        from: query.query,
        fields: query.fields || [],
        previewRows: [],
        allResultsIncluded: false,
        options: mergedVirtualGraphOptions,
      });
    });

    if (virtualGraphsToCreate.length > 0) {
      dispatch(
        projectsActionCreators.addedVirtualGraphsToProject(
          projectId,
          ...virtualGraphsToCreate
        )
      );
    }

    return virtualGraphsToCreate.map((vg) => vg.id);
  }, [
    selectedDataSource,
    selectedDataSourceType,
    checkedVgTables,
    customSqlQueries,
    vgTableMetadata,
    connection?.id,
    dispatch,
    projectId,
    mergedVirtualGraphOptions,
  ]);

  const handleCreateVg = React.useCallback(async () => {
    setIsCreating(true);
    try {
      const vgIds = await createVirtualGraphs();
      if (!isMountedRef.current) {
        return;
      }

      if (vgIds && vgIds.length) {
        resetDialogState();
        dispatch(
          uiSlice.actions.toggledProjectResourceDialog({ isOpen: false })
        );
      }
    } finally {
      if (isMountedRef.current) {
        setIsCreating(false);
      }
    }
  }, [createVirtualGraphs, dispatch, resetDialogState, isMountedRef]);

  const handleCreateAndMapVg = React.useCallback(async () => {
    if (!project?.diagram) {
      return;
    }

    setIsCreateAndMapLoading(true);
    try {
      const vgIds = await createVirtualGraphs();
      if (!isMountedRef.current) {
        return;
      }

      if (vgIds && vgIds.length) {
        const loaded = await fetchMappingSuggestions({
          selectedClasses: [],
          selectedResources: vgIds,
        });
        if (!isMountedRef.current) {
          return;
        }

        const snapshot = project?.diagram ? { ...project.diagram } : null;

        if (loaded.mappingSuggestions?.length) {
          const diagram = await handleCreateMappings(loaded.mappingSuggestions);

          if (snapshot && diagram) {
            snapshot.model = transformModelToSnapshot(diagram.model);
          }
          if (!isMountedRef.current) {
            return;
          }
        }

        resetDialogState();
        dispatch(
          uiSlice.actions.toggledProjectResourceDialog({ isOpen: false })
        );
        if (useVoiceboxSuggestions && project && snapshot) {
          navigate(getProjectRoute(projectId, { rescale: true }));
          evaluateSummary({ ...project, diagram: snapshot });
        }
      }
    } finally {
      if (isMountedRef.current) {
        setIsCreateAndMapLoading(false);
      }
    }
  }, [
    createVirtualGraphs,
    dispatch,
    evaluateSummary,
    fetchMappingSuggestions,
    handleCreateMappings,
    isMountedRef,
    navigate,
    project,
    projectId,
    resetDialogState,
    useVoiceboxSuggestions,
  ]);

  const createButtonError = React.useMemo(() => {
    if (selectedFiles.length === 0) {
      return '';
    }

    for (let i = 0; i < selectedFiles.length; i += 1) {
      const error = validationErrors[i];
      if (error) {
        const metadata = fileMetadata[i];
        const fileName =
          metadata?.fileName ||
          selectedFiles[i].name.replace(/\.csv$/i, '') ||
          copy.components.projectResourceDialog.projectFileUploadPanel.form.namelessTabLabel(
            i + 1
          );
        return copy.components.projectResourceDialog.projectFileUploadPanel.validation.errorWithName(
          error,
          fileName
        );
      }
    }

    return '';
  }, [selectedFiles, fileMetadata, validationErrors]);

  // Determine if we have VG selections (for footer options)
  const hasVgSelectionsForFooter =
    selectedDataSource &&
    (checkedVgTables.length > 0 || customSqlQueries.length > 0);

  React.useEffect(() => {
    if (fullSize) {
      return;
    }

    // VG mode footer
    if (hasVgSelectionsForFooter) {
      setFooterOptions({
        onCancel: handleCancel,
        onCreate: handleCreateVg,
        onCreateAndMap: handleCreateAndMapVg,
        isCreateLoading: isCreating,
        isCreateAndMapLoading,
      });
      return;
    }

    // File upload mode footer
    if (selectedFiles.length > 0) {
      setFooterOptions({
        onCancel: handleCancel,
        onCreate: handleCreate,
        onCreateAndMap: handleCreateAndMap,
        isCreateLoading: isCreating,
        isCreateAndMapLoading,
        createButtonError,
      });
    } else {
      setFooterOptions({});
    }
  }, [
    selectedFiles.length,
    setFooterOptions,
    handleCancel,
    handleCreate,
    handleCreateAndMap,
    handleCreateVg,
    handleCreateAndMapVg,
    isCreating,
    isCreateAndMapLoading,
    fullSize,
    fileMetadata,
    createButtonError,
    hasVgSelectionsForFooter,
  ]);

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));

    setFileMetadata((prevMetadata) => {
      const reindexedMetadata: Record<number, FileCreationMetadata> = {};

      Object.entries(prevMetadata).forEach(([key, value]) => {
        const oldIndex = Number(key);
        if (oldIndex < index) {
          reindexedMetadata[oldIndex] = value;
        } else if (oldIndex > index) {
          reindexedMetadata[oldIndex - 1] = value;
        }
      });

      return reindexedMetadata;
    });
  };

  const rootProps = getRootProps() as React.HTMLAttributes<HTMLDivElement> & {
    ref: React.Ref<HTMLDivElement>;
  };
  const inputProps =
    getInputProps() as React.InputHTMLAttributes<HTMLInputElement> & {
      ref: React.Ref<HTMLInputElement>;
    };

  // Determine if we're showing VG selection mode
  const hasVgSelections =
    selectedDataSource &&
    (checkedVgTables.length > 0 || customSqlQueries.length > 0);

  // Handle removing a VG table
  const handleRemoveVgTable = React.useCallback(
    (tableName: string) => {
      setCheckedVgTables((prev) => prev.filter((t) => t.name !== tableName));
      setVgTableMetadata((prev) => {
        const next = { ...prev };
        delete next[tableName];
        return next;
      });
    },
    [setCheckedVgTables, setVgTableMetadata]
  );

  // Handle removing a custom SQL query
  const handleRemoveCustomQuery = React.useCallback(
    (name: string) => {
      setCustomSqlQueries((prev) => prev.filter((q) => q.name !== name));
    },
    [setCustomSqlQueries]
  );

  // Handle removing all VG selections
  const handleRemoveAllVg = React.useCallback(() => {
    setSelectedDataSource(null);
    setSelectedDataSourceType(null);
    setCheckedVgTables([]);
    setVgTableMetadata({});
    setCustomSqlQueries([]);
  }, [
    setSelectedDataSource,
    setSelectedDataSourceType,
    setCheckedVgTables,
    setVgTableMetadata,
    setCustomSqlQueries,
  ]);

  // Handle adding more tables from the same data source
  const handleAddMoreVgTables = React.useCallback(() => {
    if (openPanel && selectedDataSourceType && selectedDataSource) {
      openPanel({
        props: {
          dataSourceType: selectedDataSourceType,
          skipToTables: true,
        },
        renderPanel: AddDataSourcePanel,
      });
    }
  }, [openPanel, selectedDataSourceType, selectedDataSource]);

  // Handle selecting a data source type from the grid
  const handleSelectDataSourceType = React.useCallback(
    async (dataSourceType: DataSourceType) => {
      if (!openPanel) {
        return;
      }

      // Handle sample music data source specially - create it and go to tables
      if (dataSourceType === SAMPLE_MUSIC_DATA_SOURCE_TYPE) {
        const dataSource = await createOrRefreshSampleDataSource();
        if (dataSource) {
          if (existingSampleDataSource) {
            setSelectedDataSource(existingSampleDataSource);
          }
          setSelectedDataSourceType(SAMPLE_MUSIC_DATA_SOURCE_TYPE);
          openPanel({
            props: {
              dataSourceType: SAMPLE_MUSIC_DATA_SOURCE_TYPE,
              skipToTables: true,
            },
            renderPanel: AddDataSourcePanel,
          });
        }
        return;
      }

      const dataSources = dataSourcesByType[dataSourceType] || [];

      // If no existing connections, skip directly to creation panel
      if (dataSources.length === 0) {
        openPanel({
          renderPanel: DataSourceCreationPanel,
          props: {
            dataSourceType,
            contextOverrides: {
              setDialogTitle,
              setHeaderBackButton,
              setFullSize,
              setFooterOptions,
            },
          },
        });
      } else {
        openPanel({
          props: { dataSourceType },
          renderPanel: AddDataSourcePanel,
        });
      }
    },
    [
      createOrRefreshSampleDataSource,
      dataSourcesByType,
      existingSampleDataSource,
      openPanel,
      setDialogTitle,
      setFooterOptions,
      setFullSize,
      setHeaderBackButton,
      setSelectedDataSource,
      setSelectedDataSourceType,
    ]
  );

  // If VG tables are selected, show VG cards view
  if (hasVgSelections && selectedDataSource && selectedDataSourceType) {
    const totalItems = checkedVgTables.length + customSqlQueries.length;

    return (
      <div className={styles.resourceTypeSelection}>
        <DataSourceHeaderCard
          dataSource={selectedDataSource}
          dataSourceType={selectedDataSourceType}
        />

        <Heading size="sm">
          {
            copy.components.projectResourceDialog.resourceTypePanelV2
              .dataSourceHeaderCard.selectData
          }
        </Heading>

        <div className={styles.vgSelectionContainer}>
          <div className={styles.selectedResources}>
            {checkedVgTables.map((table) => {
              const metadata = vgTableMetadata[table.name];
              // Use edited name if available, otherwise use table name
              const displayName = metadata?.editedName || table.name;
              // Use edited field labels if available
              const displayFields = metadata?.fields
                ? metadata.fields.map((field) => ({
                    ...field,
                    label:
                      metadata.editedFieldLabels?.[field.value] ?? field.label,
                  }))
                : metadata?.fields || [];
              // Create metadata with edited values for display
              const displayMetadata = metadata
                ? {
                    ...metadata,
                    fields: displayFields,
                  }
                : undefined;

              return (
                <VirtualGraphResourceCard
                  key={`table-${table.name}`}
                  dataSourceType={selectedDataSourceType}
                  metadata={displayMetadata}
                  name={displayName}
                  onRemove={() => handleRemoveVgTable(table.name)}
                  onViewPreview={() => {
                    if (openPanel && selectedDataSourceType) {
                      openPanel({
                        props: {
                          dataSourceType: selectedDataSourceType,
                          initialPreviewTable: table.name,
                        },
                        renderPanel: AddDataSourcePanel,
                      });
                    }
                  }}
                />
              );
            })}
            {customSqlQueries.map((query) => (
              <VirtualGraphResourceCard
                key={`query-${query.name}`}
                dataSourceType={selectedDataSourceType}
                metadata={
                  query.fields
                    ? {
                        tableName: query.name,
                        fields: query.fields,
                        rowCount: query.rowCount ?? query.fields.length,
                      }
                    : undefined
                }
                name={query.name}
                onRemove={() => handleRemoveCustomQuery(query.name)}
                onViewPreview={() => {
                  if (openPanel && selectedDataSourceType) {
                    openPanel({
                      props: {
                        dataSourceType: selectedDataSourceType,
                        initialCustomQuery: query.name,
                      },
                      renderPanel: AddDataSourcePanel,
                    });
                  }
                }}
              />
            ))}
          </div>

          <div className={styles.vgSelectionHeader}>
            <Button
              onClick={handleAddMoreVgTables}
              size="sm"
              variant="secondary"
            >
              {
                copy.components.projectResourceDialog.resourceTypePanelV2
                  .dataSourceHeaderCard.addMore
              }
            </Button>
            <Button onClick={handleRemoveAllVg} size="sm" variant="ghost-gray">
              {copy.components.projectResourceDialog.resourceTypePanelV2.dataSourceHeaderCard.removeAll(
                totalItems
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.resourceTypeSelection}>
      <div
        ref={rootProps.ref}
        className={classNames(styles.fileUploadArea, {
          [styles.active]: isDragActive,
        })}
        onClick={rootProps.onClick}
        onDragEnter={rootProps.onDragEnter}
        onDragLeave={rootProps.onDragLeave}
        onDragOver={rootProps.onDragOver}
        onDrop={rootProps.onDrop}
        role="button"
        tabIndex={0}
      >
        <input
          ref={inputProps.ref}
          accept={inputProps.accept}
          data-testid={TEST_IDS.dropzone.fileInput}
          multiple={inputProps.multiple}
          onChange={inputProps.onChange}
          style={inputProps.style}
          type={inputProps.type}
        />
        <div className={styles.fileUploadAreaIcon}>
          <Icon IconComponent={CloudUpload} size="xl" />
        </div>
        <Body className={styles.fileUploadAreaText} size="md">
          {
            copy.components.projectResourceDialog.resourceTypePanelV2.fileUpload
              .dragText
          }
        </Body>
        <Body className={styles.fileUploadAreaSupportText} size="sm">
          {
            copy.components.projectResourceDialog.resourceTypePanelV2.fileUpload
              .supportText
          }
        </Body>
        <Button size="md" variant="secondary">
          {
            copy.components.projectResourceDialog.resourceTypePanelV2.fileUpload
              .button
          }
        </Button>
      </div>

      {selectedFiles.length > 0 ? (
        <div className={styles.selectedResources}>
          {selectedFiles.map((file, index) => {
            const fileId = getFileId(file);
            return (
              <ProjectResourceCard
                key={fileId}
                fileId={fileId}
                index={index}
                metadata={fileMetadata[index]}
                onRemove={handleRemoveFile}
                onViewPreview={handleOpenFilePanel}
                resourceIcon={Document}
                resourceName={
                  fileMetadata[index]?.fileName ||
                  file.name.replace(/\.csv$/i, '')
                }
                validationError={validationErrors[index]}
              />
            );
          })}
        </div>
      ) : null}

      {selectedFiles.length === 0 ? (
        <div className={styles.dataSourceSection}>
          <Heading className={styles.dataSourceSectionTitle} size="md">
            {
              copy.components.projectResourceDialog.resourceTypePanelV2
                .dataSourceSection.title
            }
          </Heading>

          {isLoadingDataSources ? (
            <div className={styles.dataSourceGridPlaceholder}>
              <Loader size={48} />
            </div>
          ) : null}

          {!isLoadingDataSources && dataSourceTypes.length === 0 ? (
            <Body className={styles.dataSourceGridPlaceholder} size="sm">
              {
                copy.components.projectResourceDialog
                  .dataSourceTypeSelectionPanel.noDataSources
              }
            </Body>
          ) : null}

          {!isLoadingDataSources && dataSourceTypes.length > 0 ? (
            <DataSourceTypeGrid
              dataSourceTypes={dataSourceTypes}
              isSampleLoading={isSampleDataSourceLoading}
              onSelectType={handleSelectDataSourceType}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
